import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Alert, Box, Container, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React, { ReactNode, useState } from 'react';
import { useSearchParams } from 'react-router';
import { z } from 'zod';
import { resetPasswordMutation } from '../../../@api-client/@tanstack/react-query.gen';
import { useForm } from '../../hooks/use-form';
import { ControlledTextField } from '../../inputs/forms/controlled-text-field/controlled-text-field';
import { Button } from '../../primitives/button';
import { Typography } from '../../primitives/typography/typography';
import { AuthLayout } from '../auth-layout/auth-layout';
import { AuthLayoutIduFooter } from '../auth-layout/components/auth-layout-idu-footer';

// Validation schema
const forgotPasswordSchema = z.object({
  email: z.email('Invalid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

type ForgotPasswordPageProps = {
  logo?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  hideFooter?: boolean;
  instructions?: ReactNode;
};

export const ForgotPasswordPage = ({
  logo: Logo,
  hideFooter,
  instructions,
}: ForgotPasswordPageProps) => {
  const [searchParams, _setSearchParams] = useSearchParams();
  const email = searchParams.get('email');

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isDataEntryPhase = !error && !success;

  const { mutateAsync } = useMutation({
    ...resetPasswordMutation({}),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: email || '',
    },
    unsavedChangesGuard: false,
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setError(null);
      setSuccess(false);

      await mutateAsync({
        body: {
          email: data.email,
          continueUrl: `${window.location.origin}/reset-password`,
        },
      });

      setSuccess(true);
    } catch (err: any) {
      console.error('Forgot password error:', err);
      setError('Failed to send password reset email. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AuthLayout
        main={
          <>
            <Box sx={{ pb: 1 }}>{Logo && <Logo style={{ width: 200, height: 150 }} />}</Box>
            {isDataEntryPhase && Boolean(instructions) && instructions}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography.Body>Email sent</Typography.Body>
                <Typography.Subtitle>
                  Password reset instructions have been sent to your email address. Please check
                  your inbox for an email from iDU.
                </Typography.Subtitle>
              </Alert>
            )}
            {isDataEntryPhase && (
              <>
                <ControlledTextField
                  name="email"
                  control={control}
                  disabled={success}
                  label="Email"
                  fullWidth
                  error={!!errors.email}
                  type="email"
                />

                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  disabled={isSubmitting || success}
                  loadingPosition="start"
                  loading={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send reset instructions'}
                </Button>
              </>
            )}
            <Button to="/login" startIcon={<ArrowBackIcon />} size="large">
              Back to Sign In
            </Button>
          </>
        }
        footer={!hideFooter && <AuthLayoutIduFooter />}
      />
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Stack
              spacing={2}
              direction="column"
              sx={{ minHeight: '100vh', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                <Stack
                  spacing={1}
                  direction="column"
                  sx={{ justifyContent: 'center', alignItems: 'center', width: '100%' }}
                ></Stack>
              </Stack>
              <Stack direction="column" sx={{ flex: 0, alignItems: 'center', pb: 4 }}></Stack>
            </Stack>
          </Box>
        </Box>
      </Container>
    </form>
  );
};

export default ForgotPasswordPage;
